import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { EmailSummaryChart } from './EmailSummaryChart';
import { ChevronLeft, ChevronRight, Loader2, Mail, Paperclip, RotateCw, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;
const fmt = (iso) => new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });

const StatusBadge = ({ status }) => (status === 'sent'
  ? <Badge className="bg-emerald-100 text-emerald-700">Gönderildi</Badge>
  : <Badge className="bg-rose-100 text-rose-700">Başarısız</Badge>);

const ResendButton = ({ log, onResent, size = 'sm' }) => {
  const [busy, setBusy] = useState(false);
  if (log.status !== 'failed') return null;
  if (log.resent_at) return <Badge className="bg-emerald-50 text-emerald-700 whitespace-nowrap">Tekrar gönderildi ✓</Badge>;
  const blocked = log.resend_block_reason;
  const resend = async (e) => {
    e.stopPropagation();
    if (blocked) { toast.warning(blocked); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/settings/email-logs/${log.id}/resend`);
      toast.success('E-posta tekrar gönderildi');
      onResent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tekrar gönderilemedi');
      onResent();
    } finally { setBusy(false); }
  };
  return (
    <Button size={size} variant="outline" onClick={resend} disabled={busy} title={blocked || undefined} className={blocked ? 'opacity-50' : ''} data-testid={`email-log-resend-${log.id}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />}Tekrar Gönder
    </Button>
  );
};

const LogDetail = ({ id, onClose, onResent }) => {
  const [log, setLog] = useState(null);
  useEffect(() => {
    if (id) { setLog(null); axios.get(`${API}/settings/email-logs/${id}`).then(res => setLog(res.data)).catch(() => onClose()); }
  }, [id, onClose]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-log-detail">
        <DialogHeader><DialogTitle className="pr-6">{log?.subject || 'E-posta'}</DialogTitle></DialogHeader>
        {!log ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="space-y-3">
            <div className="text-sm text-slate-600 space-y-1">
              <p><b>Alıcı:</b> {log.to.join(', ')}{log.cc?.length ? ` · CC: ${log.cc.join(', ')}` : ''}</p>
              <p className="flex items-center gap-2 flex-wrap"><b>Tarih:</b> {fmt(log.created_at)} <StatusBadge status={log.status} /> <Badge variant="outline">{log.category}</Badge>{log.has_attachment && <Badge variant="outline"><Paperclip className="h-3 w-3 mr-1" />Ekli dosya</Badge>}</p>
              {log.error && <p className="text-rose-600" data-testid="email-log-error"><b>Hata:</b> {log.error}</p>}
              {log.resend_of && <p className="text-xs text-slate-500">Tekrar gönderim ({log.resent_by})</p>}
              <ResendButton log={log} onResent={() => { onResent(); onClose(); }} />
              {log.resend_block_reason && !log.resent_at && <p className="text-xs text-amber-700" data-testid="email-log-resend-reason">{log.resend_block_reason}</p>}
            </div>
            <iframe title="E-posta içeriği" srcDoc={log.html} sandbox="" className="w-full h-[55vh] rounded border bg-white" data-testid="email-log-frame" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EmailLogsCard = () => {
  const [filters, setFilters] = useState({ q: '', status: 'all', category: 'all', date_from: '', date_to: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params[k] = v; });
    axios.get(`${API}/settings/email-logs`, { params }).then(res => setData(res.data)).catch(() => setData({ items: [], total: 0, categories: [] }));
  }, [filters, page, refreshKey]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const resendAll = async () => {
    setBulkBusy(true);
    const params = {};
    ['q', 'category', 'date_from', 'date_to'].forEach(k => { if (filters[k] && filters[k] !== 'all') params[k] = filters[k]; });
    try {
      const res = await axios.post(`${API}/settings/email-logs/resend-failed`, null, { params });
      const { sent, failed, remaining } = res.data;
      if (!sent && !failed) toast.info('Tekrar gönderilebilecek başarısız e-posta yok');
      else toast[failed ? 'warning' : 'success'](`${sent} e-posta tekrar gönderildi${failed ? `, ${failed} tanesi yine başarısız` : ''}${remaining ? `. ${remaining} e-posta kaldı, tekrar basın.` : ''}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tekrar gönderilemedi');
    } finally { setBulkBusy(false); }
  };

  const set = (patch) => { setPage(1); setFilters(f => ({ ...f, ...patch })); };
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const closeDetail = useCallback(() => setOpenId(null), []);

  return (
    <Card className="border-slate-200" data-testid="email-logs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />E-posta Gönderim Geçmişi</CardTitle>
        <CardDescription>Sistemden giden tüm e-postalar. Kayıtlar 180 gün saklanır, sonra otomatik silinir.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <EmailSummaryChart refreshKey={refreshKey} />
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={filters.q} onChange={(e) => set({ q: e.target.value })} placeholder="Alıcı veya konu ara" className="pl-8" data-testid="email-logs-search" />
          </div>
          <Select value={filters.status} onValueChange={(v) => set({ status: v })}>
            <SelectTrigger data-testid="email-logs-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              <SelectItem value="sent">Gönderildi</SelectItem>
              <SelectItem value="failed">Başarısız</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger data-testid="email-logs-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm türler</SelectItem>
              {(data?.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-1 sm:col-span-2 lg:col-span-2">
            <Input type="date" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} title="Başlangıç" data-testid="email-logs-from" />
            <Input type="date" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} title="Bitiş" data-testid="email-logs-to" />
          </div>
        </div>
        {filters.status === 'failed' && data?.total > 0 && (
          <Button size="sm" onClick={resendAll} disabled={bulkBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="email-logs-resend-all">
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />}Tümünü Tekrar Gönder
          </Button>
        )}
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : data.items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="email-logs-empty">Kayıt bulunamadı.</p>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full table-fixed text-sm" data-testid="email-logs-table">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr><th className="text-left px-3 py-2 w-36">Tarih</th><th className="text-left px-3 py-2 w-[22%]">Alıcı</th><th className="text-left px-3 py-2">Konu</th><th className="text-left px-3 py-2 w-32 hidden md:table-cell">Tür</th><th className="text-left px-3 py-2 w-32">Durum</th><th className="px-3 py-2 w-40" /></tr>
              </thead>
              <tbody className="divide-y">
                {data.items.map(l => (
                  <tr key={l.id} onClick={() => setOpenId(l.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`email-log-row-${l.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmt(l.created_at)}</td>
                    <td className="px-3 py-2 truncate" title={l.to.join(', ')}>{l.to.join(', ')}</td>
                    <td className="px-3 py-2 truncate" title={l.subject}>{l.has_attachment && <Paperclip className="h-3 w-3 inline mr-1 text-slate-400" />}{l.subject}</td>
                    <td className="px-3 py-2 truncate text-slate-600 hidden md:table-cell">{l.category}</td>
                    <td className="px-3 py-2"><StatusBadge status={l.status} />{l.resend_of && <span className="block text-[10px] text-slate-500 mt-0.5">Tekrar gönderim</span>}</td>
                    <td className="px-3 py-2 text-right"><ResendButton log={l} onResent={refresh} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data?.total > 0 && (
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="email-logs-total">{data.total} kayıt</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="email-logs-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span>{page} / {pages}</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="email-logs-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </CardContent>
      <LogDetail id={openId} onClose={closeDetail} onResent={refresh} />
    </Card>
  );
};
